/**
 * Pure H OS · Enterprise Document Engine · Document Center.
 *
 * A single index of every enterprise document registered on the backend
 * (`GET /api/pureh/documents/registry`). Groups by category, offers
 * Preview / PDF / XLSX / CSV / Print actions per row, and links to the
 * originating source module where applicable.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar } from "../components/lib.overlay";
import { DocumentActions } from "../components/DocumentActions";

type DocEntry = { key: string; title: string; category: string; confidential: boolean };
type Tab = "all" | "Finance" | "R&D" | "Marketing" | "Executive";

declare const process: { env: Record<string, string | undefined> };

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

export default function DocumentCenter() {
  const [items, setItems] = useState<DocEntry[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("all");

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch(`${BASE}/documents/registry`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setItems(await res.json() as DocEntry[]);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Failed to load registry");
      }
    })();
  }, []);

  const filtered = useMemo(() => (tab === "all" ? items : items.filter((d) => d.category === tab)), [items, tab]);
  const countByCat = useMemo(() => {
    const c: Record<string, number> = {};
    items.forEach((d) => { c[d.category] = (c[d.category] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="doc-center-page">
      <Card testId="dc-filters">
        <SectionHeader eyebrow="Enterprise Document Engine" title={`${items.length} document type${items.length === 1 ? "" : "s"}`} />
        <div className="text-xs mb-3" style={{ color: "var(--ph-muted)" }}>
          Preview · PDF (WeasyPrint · A4/Letter · portrait/landscape) · Excel (XLSX) · CSV · Print. Company branding, timestamp, and confidential watermark applied server-side.
        </div>
        <TabBar<Tab>
          testId="dc-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${items.length}` },
            { key: "Finance",   label: `Finance ${countByCat.Finance ?? 0}` },
            { key: "R&D",       label: `R&D ${countByCat["R&D"] ?? 0}` },
            { key: "Marketing", label: `Marketing ${countByCat.Marketing ?? 0}` },
            { key: "Executive", label: `Executive ${countByCat.Executive ?? 0}` },
          ]}
        />
      </Card>

      {err && (
        <Card testId="dc-err">
          <div className="text-sm" style={{ color: "var(--ph-danger)" }}>Failed to load registry: {err}</div>
        </Card>
      )}

      <Card padded={false} testId="dc-list">
        {filtered.map((d) => (
          <div key={d.key} data-testid={`dc-row-${d.key}`}
            className="grid grid-cols-12 items-center px-5 py-3 text-sm gap-3"
            style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
            <div className="col-span-2 text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>{d.category}</div>
            <div className="col-span-4">
              <div style={{ color: "var(--ph-text)" }}>{d.title}</div>
              <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}><code>{d.key}</code></div>
            </div>
            <div className="col-span-1">
              {d.confidential && <SeverityBadge severity="high" />}
            </div>
            <div className="col-span-5 flex justify-end">
              <DocumentActions docKey={d.key} compact testId={`dc-actions-${d.key}`} />
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
